// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { AssistantEvent, AssistantTransport, PageContext } from "@/features/assistant/types";

// Prototype-only agent. Answers are English fixture data shaped around the page
// context label; nothing is fetched. A real SSE transport replaces this module.

export type ScenarioId = "imports" | "settings" | "stores" | "noAccess" | "error" | "long" | "fallback";

export const SCENARIO_PROMPTS: Record<Exclude<ScenarioId, "fallback">, string> = {
  imports: "Which stores had a failed import this week?",
  settings: "Which settings differ from the chain default?",
  stores: "How many stores are live, and in which regions?",
  noAccess: "What's the Wi-Fi password for store GB-0412?",
  error: "Simulate a timeout error",
  long: "Explain how store settings inherit from the chain",
};

const MATCHERS: [ScenarioId, RegExp][] = [
  ["error", /\b(timeout|error)\b/i],
  ["noAccess", /password|credential/i],
  ["imports", /import/i],
  ["settings", /setting.*(differ|default)|(differ|default).*setting/i],
  ["long", /explain|inherit/i],
  ["stores", /\blive\b|how many|region/i],
];

export function matchScenario(question: string): ScenarioId {
  return MATCHERS.find(([, re]) => re.test(question))?.[0] ?? "fallback";
}

type Scripted = { event: AssistantEvent; ms: number };

const step = (id: string, running: string, done: string, ms: number): Scripted[] => [
  { event: { type: "step", id, label: running, status: "running" }, ms: 0 },
  { event: { type: "step", id, label: done, status: "done" }, ms },
];

function words(text: string, msPerWord = 35): Scripted[] {
  return text.split(/(?<= )/).map((delta) => ({ event: { type: "text", delta }, ms: msPerWord }));
}

const done: Scripted = { event: { type: "done" }, ms: 0 };

function script(id: ScenarioId, context: PageContext | null): Scripted[] {
  const scope = context?.label ?? "all chains";
  switch (id) {
    case "imports":
      return [
        ...step("imports", "Checking import history…", `Checked import history for 214 stores in ${scope}`, 1100),
        ...words(`3 stores in ${scope} had a failed import since Monday. Two failed on the same planogram file.`),
        {
          event: {
            type: "table",
            table: {
              columns: ["Store", "Import", "When"],
              rows: [
                ["GB-0412 Leeds", "Planogram · failed", "Tue 09:14"],
                ["GB-0418 York", "Planogram · failed", "Tue 09:15"],
                ["GB-1033 Hull", "Products · failed", "Thu 22:40"],
              ],
            },
          },
          ms: 150,
        },
        {
          event: {
            type: "link",
            link:
              context?.kind === "chain"
                ? { label: `Open imports for ${scope}`, href: `${context.path}?t=imports` }
                : { label: "Open imports", href: "/ingress/imports" },
          },
          ms: 100,
        },
        done,
      ];
    case "settings":
      return [
        ...step("read", "Reading settings…", `Read 312 settings for ${scope}`, 700),
        ...step("compare", "Comparing store values with chain defaults…", "Compared 312 settings", 1200),
        ...words(
          "17 settings differ from the chain default. Most are in Scheduling (9), mainly custom opening hours for stores in Scotland.",
        ),
        {
          event: {
            type: "table",
            table: {
              columns: ["Group", "Differs"],
              rows: [
                ["Scheduling", "9"],
                ["Imports", "5"],
                ["Hardware", "3"],
              ],
            },
          },
          ms: 150,
        },
        done,
      ];
    case "stores":
      return [
        ...step("count", "Counting stores…", `Counted stores in ${scope}`, 800),
        ...words(
          `${scope} has 214 stores, 208 of them live. North: 71, Midlands: 64, South: 73. The 6 not live are still onboarding.`,
        ),
        done,
      ];
    case "noAccess":
      return [
        ...step("check", "Checking what you can see…", "Checked your access", 600),
        {
          event: {
            type: "note",
            text: "I can't see hardware credentials with your access. The store's Hardware tab shows what you're allowed to view.",
          },
          ms: 200,
        },
        {
          event: { type: "link", link: { label: "Open GB-0412 → Hardware", href: "/stores/GB-0412?t=hardware" } },
          ms: 100,
        },
        done,
      ];
    case "error":
      return [
        ...step("try", "Asking the data service…", "Asked the data service", 1500),
        { event: { type: "error", message: "The assistant didn't respond in time. Your question is saved." }, ms: 300 },
      ];
    case "long":
      return [
        ...step("docs", "Reading the settings model…", "Read the settings model", 600),
        ...words(
          "Every setting has a definition with a default. A chain can override the default, and a store can override the chain. " +
            "When BackOffice shows a store value it walks that chain: store override first, then the chain value, then the definition default. " +
            "Regions do not hold values of their own; they only group stores for bulk edits. " +
            "This is why a change on the chain appears on every store that has no override, and why a store override survives chain changes until someone clears it. " +
            "The Settings tab marks inherited values in grey and overrides in the normal text colour, so you can tell at a glance where a value comes from.",
          45,
        ),
        done,
      ];
    case "fallback":
      return [
        ...words(
          "In this prototype I can answer about imports, settings differences, store counts and access. Try one of the suggestions.",
        ),
        done,
      ];
  }
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

export function createMockTransport({ speed = 1 }: { speed?: number } = {}): AssistantTransport {
  return {
    async *ask(request, signal) {
      for (const { event, ms } of script(matchScenario(request.question), request.context)) {
        await sleep(ms * speed, signal);
        if (signal.aborted) return;
        yield event;
      }
    },
    sendRating: async () => "mock-feedback",
    sendComment: async () => {},
    sendRatingEvent: async () => {},
  };
}
